import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import NotificationCard from '../components/student/NotificationCard';
import { useStudent } from '../components/student/StudentContext';
import './Notifications.css';

// Student Notification Center. Every item is DERIVED from a real record on the server (announcements, assigned /
// scheduled assessments, submitted results — see server/services/notificationService.js); only the read marker is
// persisted. Nothing here is generated or stored in the browser.
export default function Notifications() {
  const navigate = useNavigate();
  const { setUnread } = useStudent();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await API.get('/portal/notifications');
      setItems(res.data.notifications);
      setUnread(res.data.unread);
    } catch (err) {
      setError(err);
    }
  }, [setUnread]);

  useEffect(() => { load(); }, [load]);

  const unreadCount = useMemo(() => (items || []).filter(n => !n.read).length, [items]);
  const shown = useMemo(() => (items || []).filter(n => filter === 'all' || !n.read), [items, filter]);

  const markRead = async (n) => {
    if (n.read) return;
    setItems(list => list.map(x => (x.id === n.id ? { ...x, read: true } : x)));     // optimistic; reconciled below
    try {
      const res = await API.post('/portal/notifications/read', { ids: [n.id] });
      setUnread(res.data.unread);
    } catch {
      setItems(list => list.map(x => (x.id === n.id ? { ...x, read: false } : x)));  // it did not persist — show the truth
    }
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await API.post('/portal/notifications/read-all', {});
      setItems(list => list.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* leave as is; the button is still available */ } finally { setBusy(false); }
  };

  const toggle = (n) => { setOpenId(id => (id === n.id ? null : n.id)); markRead(n); };
  const open = (n) => navigate(n.action.path);

  return (
    <div className="z-page z-page--narrow notif-page">
      <div className="notif-toolbar">
        <div className="z-segments" role="tablist" aria-label="Filter notifications">
          <button type="button" role="tab" className="z-segment" aria-selected={filter === 'all'} onClick={() => setFilter('all')}>All</button>
          <button type="button" role="tab" className="z-segment" aria-selected={filter === 'unread'} onClick={() => setFilter('unread')}>
            Unread{unreadCount > 0 && <span className="z-chip z-chip--violet notif-count">{unreadCount}</span>}
          </button>
        </div>
        <button type="button" className="z-btn z-btn--ghost z-btn--sm" onClick={markAll} disabled={busy || unreadCount === 0}>
          <Icon name="check" size={16} /> Mark all read
        </button>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : items === null ? (
        <div className="notif-list" role="status" aria-label="Loading notifications">
          {[0, 1, 2].map(i => <Skeleton key={i} height={92} radius={16} />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon="notification" title="Nothing new yet" body="New assessments, placement updates and results will show up here." />
      ) : shown.length === 0 ? (
        <EmptyState icon="success" art={false} tone="neutral" compact title="You're all caught up" body="No unread notifications." action={{ label: 'Show all', secondary: true, onClick: () => setFilter('all') }} />
      ) : (
        <div className="notif-list">
          {shown.map(n => <NotificationCard key={n.id} n={n} expanded={openId === n.id} onToggle={() => toggle(n)} onOpen={open} />)}
        </div>
      )}
    </div>
  );
}
